"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MapPin, Mail, Phone, ShieldCheck, Zap, Globe, Loader, Send } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-md bg-primary/10 flex items-center justify-center mb-4 border border-primary/30 shadow-lg shadow-primary/20">
          <ShieldCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/50 hover:bg-primary/10 rounded-md">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background/50 border-b-2 border-x-0 border-t-0 border-border/50 focus-visible:ring-0 focus-visible:border-primary rounded-md transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-serif">Enter your legal or corporate name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid corporate email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Corporate Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="j.doe@enterprise.com" className="bg-background/50 border-b-2 border-x-0 border-t-0 border-border/50 focus-visible:ring-0 focus-visible:border-primary rounded-md transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-serif">We will respond via secure channel.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="subject" rules={{
        required: "Please select a service area"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Service Area</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Managed IT Support" className="bg-background/50 border-b-2 border-x-0 border-t-0 border-border/50 focus-visible:ring-0 focus-visible:border-primary rounded-md transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground">Contact Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+971 00 000 0000" className="bg-background/50 border-b-2 border-x-0 border-t-0 border-border/50 focus-visible:ring-0 focus-visible:border-primary rounded-md transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-xs font-serif">For urgent infrastructure audits.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please describe your requirements"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Project Brief</FormLabel>
            <FormControl>
              <textarea placeholder="Describe your current infrastructure challenges..." className="flex w-full min-w-0 rounded-md border-b-2 border-x-0 border-t-0 border-border/50 bg-background/50 px-3 py-2 text-base shadow-xs transition-all outline-none placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm min-h-[120px] resize-y aria-invalid:border-destructive" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <div className="text-destructive mt-0.5"></div>
          <p className="text-destructive text-sm font-serif">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] h-12 bg-primary text-primary-foreground font-default font-bold uppercase tracking-wider rounded-md shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            <Send className="mr-2 size-5" />
            Submit Request
          </>}
      </Button>
    </Form>;
}
export default function ContactBetnav() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="221642" id="secure-your-perimeter" className="bg-background text-foreground relative overflow-hidden py-24">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-primary/10 rounded-md blur-3xl" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[40%] bg-secondary/5 rounded-md blur-3xl" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTAgNDBMMTQwIDBIMTQwTDQwIDQwSDB6IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDIpIi8+PC9zdmc+')] opacity-30" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-7xl">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-md bg-primary/10 border border-primary/30 text-primary mb-6">
            <Zap className="size-4" />
            <span className="text-xs font-mono font-bold tracking-widest uppercase">Enterprise Grade Support</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-default font-bold mb-6 tracking-tighter uppercase">
            Secure Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Perimeter</span>
          </h2>
          <p className="text-lg text-muted-foreground font-serif">
            Architecting resilient, cost-effective infrastructure that scales with your ambition. Connect with our UAE-based specialists for a strategic IT audit.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-start transition-all duration-1000 delay-200 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-5 space-y-8">
            <div className="relative p-8 rounded-md bg-card/30 border border-border backdrop-blur-sm overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <h3 className="text-2xl font-default font-bold mb-8 flex items-center gap-3 uppercase tracking-tight">
                <Globe className="size-6 text-primary" />
                Security Checklist
              </h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="mt-1 p-2.5 rounded-md bg-primary/10 border border-primary/20 shadow-sm">
                    <ShieldCheck className="size-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-default font-semibold text-foreground mb-1">Audit Scheduled</h4>
                    <p className="text-muted-foreground text-sm font-serif leading-relaxed">
                      Initial infrastructure assessment scheduled within 24 hours of submission.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-1 p-2.5 rounded-md bg-primary/10 border border-primary/20 shadow-sm">
                    <Mail className="size-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-default font-semibold text-foreground mb-1">NDA Provided</h4>
                    <p className="text-muted-foreground text-sm font-serif">
                      Standard non-disclosure agreement issued before technical data exchange.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-1 p-2.5 rounded-md bg-primary/10 border border-primary/20 shadow-sm">
                    <MapPin className="size-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-default font-semibold text-foreground mb-1">Regional HQ</h4>
                    <p className="text-muted-foreground text-sm font-serif">
                      Dubai, United Arab Emirates<br />
                      Precision Technology Center
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-border">
                <h4 className="font-default font-semibold text-foreground mb-4 uppercase text-xs tracking-widest">Connect with Masters</h4>
                <div className="flex gap-4">
                  {[1, 2, 3].map((_, i) => <Link key={i} to="/ConnectPage" className="size-10 rounded-md bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 hover:shadow-lg hover:shadow-primary/20 transition-all duration-300" aria-label="Social Media Link">
                      <div className="size-4 rounded-sm bg-current opacity-80" />
                    </Link>)}
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="relative p-1 rounded-md bg-gradient-to-b from-border to-transparent">
              <div className="absolute inset-0 bg-card/40 backdrop-blur-xl rounded-md shadow-2xl" />
              <div className="relative bg-card/60 backdrop-blur-md p-8 sm:p-10 rounded-md border border-border">
                <div className="mb-8">
                  <h3 className="text-2xl font-default font-bold mb-2 uppercase tracking-tight">Initiate Protocol</h3>
                  <p className="text-muted-foreground text-sm font-serif">
                    Complete the secure transmission form below. Our engineering team will respond within one business cycle.
                  </p>
                </div>

                <FormProvider formKey="perimeter_security_form_connect" formId="perimeter_security_form_connect" sectionName="ContactITMasters" defaultValues={{
                name: "",
                email: "",
                subject: "",
                message: "",
                phone: ""
              }} submitText="Submit Request" successMessage="Transmission successful. An IT Masters architect will contact you shortly to schedule your audit." errorMessage="Transmission failed. Please verify your secure connection and try again.">
                  <ContactFormInner />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}